import type { Store } from 'redux';

/**
 * Wait for a Redux state predicate to be satisfied
 * @param store - The Redux store instance
 * @param predicate - Function that returns true when the condition is met
 * @param timeout - Optional timeout in milliseconds (default: no timeout)
 * @returns Promise that resolves with the state when predicate is satisfied
 */
export function waitForReduxState<T = any>(
  store: Store<T>,
  predicate: (state: T) => boolean,
  timeout?: number
): Promise<T>;
export function waitForReduxState<S>(
  store: Store<S>,
  predicate: (state: S) => boolean,
  timeout?: number
): Promise<S>;
export function waitForReduxState<T = any>(
  store: Store<T>,
  predicate: (state: T) => boolean,
  timeout?: number
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let timeoutId: NodeJS.Timeout | undefined;

    const checkAndResolve = () => {
      try {
        const state = store.getState();
        if (predicate(state as T)) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          unsubscribe();
          resolve(state as T);
        }
      } catch (error) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        unsubscribe();
        reject(error);
      }
    };

    const unsubscribe = store.subscribe(checkAndResolve);

    // Set timeout if specified
    if (timeout) {
      timeoutId = setTimeout(() => {
        unsubscribe();
        reject(
          new Error(`Timeout waiting for Redux state predicate (${timeout}ms)`)
        );
      }, timeout);
    }

    // Check immediately in case condition is already satisfied
    checkAndResolve();
  });
}
